import * as dtv from "../../../lib/data/values.js";
import { i18n } from "../../../lib/globalization/i18n.js";
import { Module } from "../../../lib/ui/Module.js";
import { Button } from "../../../lib/ui/controls/Button.js";
import { ModalDialog } from "../../../lib/ui/controls/dialogs/ModalDialog.js";
import { Text } from "../../../lib/ui/controls/Text.js";
import { TextBox } from "../../../lib/ui/controls/TextBox.js";
import { Tabs } from "../../../lib/ui/controls/Tabs.js";
import { openFileContentInNewWindow } from "./utils/file-content.js";
import {
    FILE_REPOSITORY_PERMISSIONS,
    canManageFileRepositoryItem,
    canModifyFileRepositoryItem,
    canOpenFileRepositoryItem,
    canWriteFileRepositoryItems,
} from "./utils/file-repository-permissions.js";
import { Grid } from "../../../lib/ui/controls/Grid.js";
import { FolderPermissionPanel } from "./components/FolderPermissionPanel.js";

export class FileRepositoryBrowser extends Module {

    static RENDER_THUMNAILS = 'thumbnails';
    static RENDER_DETAILS = 'details';
    static TAB_CONTENT = 'content';
    static TAB_PERMISSIONS = 'permissions';

    static PERMISSIONS = FILE_REPOSITORY_PERMISSIONS;
    constructor(parent, options = {}) {

        options = Object.assign({
            moduleName: 'FileRepositoryBrowser',
            moduleTitle: 'Úložiště souborů',
            renderMode: FileRepositoryBrowser.RENDER_DETAILS,
        }, options || {});

        super(parent, options);

        this.fileRepositoryManager = null;
        this.folderData = null;
        this.folderSubfolders = [];
        this.folderFiles = [];
        this.loadError = null;
        this.isLoading = false;

        this.activeTabKey = FileRepositoryBrowser.TAB_CONTENT;
        this.panelTabs = new Tabs(this, {
            activeTabKey: () => this.activeTabKey,
            onActiveTabKeyChange: (key) => {
                this.activeTabKey = key;
                this.invalidate();
            },
            tabs: [
                { key: FileRepositoryBrowser.TAB_CONTENT, label: 'Obsah složky' },
                { key: FileRepositoryBrowser.TAB_PERMISSIONS, label: 'Oprávnění' },
            ],
        });

        this.folderPermissionPanel = new FolderPermissionPanel(this, {
            getFolderData: () => this.folderData,
            getFileRepositoryManager: () => this.getFileRepositoryManager(),
            onOpenFolder: (folderUid) => this.openFolder(folderUid),
            onReloadRequested: async () => this.reloadCurrentFolder(),
        });

    };

    // --------------------------------------------------------------------------------------------

    getActiveFolderUid() {
        return this.getApp()?.getActiveRoute()?.params?.uid || null;
    };

    getCurrentFolderLabel() {
        return this.folderData?.name || 'Kořenová složka';
    };

    getFolderPath() {

        const folderPath = this.folderData?.folderPath;
        if (Array.isArray(folderPath) && folderPath.length > 0)
            return folderPath;

        if (!this.folderData)
            return [];

        return [{
            uid: this.folderData.uid || null,
            name: this.getCurrentFolderLabel(),
        }];

    };

    getParentFolderUid() {

        const folderPath = this.getFolderPath();
        if (folderPath.length >= 2)
            return folderPath[folderPath.length - 2]?.uid || null;

        return this.folderData?.parentFolderUid || null;

    };

    getActivePanelTab() {
        return this.activeTabKey || FileRepositoryBrowser.TAB_CONTENT;
    };

    getFileRepositoryManager() {

        if (!this.fileRepositoryManager)
            this.fileRepositoryManager = this.getServices().createFileRepositoryManager();

        return this.fileRepositoryManager;

    };

    canUserOpenFolder(folder) {
        return canOpenFileRepositoryItem(folder);
    };

    canUserOpenFile(file) {
        return canOpenFileRepositoryItem(file);
    };

    canUserWriteItems(folder = this.folderData) {
        return canWriteFileRepositoryItems(folder);
    };

    canUserManageItems(folder = this.folderData) {
        if (!folder)
            return false;
        return this.canUserWriteItems(folder);
    };

    isRootFolder(folder = this.folderData) {
        return Array.isArray(folder?.folderPath) && folder.folderPath.length === 1;
    };

    canUserUploadFiles(folder = this.folderData) {
        if (!folder)
            return false;
        if (this.isRootFolder(folder))
            return false;
        return this.canUserWriteItems(folder);
    };

    canUserDeleteFolder(folder) {
        if (!folder?.uid)
            return false;
        return canManageFileRepositoryItem(folder);
    };

    canUserEditFolder(folder) {
        if (!folder?.uid)
            return false;
        return canModifyFileRepositoryItem(folder);
    };

    canUserDeleteFile(file) {
        if (!file?.uid)
            return false;
        return canManageFileRepositoryItem(file);
    };

    canUserEditFile(file) {
        if (!file?.uid)
            return false;
        return canWriteFileRepositoryItems(file);
    };

    async reloadCurrentFolder() {

        const app = this.getApp();
        const params = app?.getActiveRoute()?.params || {};

        this.isLoading = true;
        this.loadError = null;
        this.invalidate();
        app?.setBusyFlag?.();

        try {
            await this.refresh(params);
            this.invalidate();
        } catch (error) {
            this.handleError(error);
        } finally {
            app?.clearBusyFlag?.();
        }

    };

    async uploadFiles() {

        const folder = this.folderData || null;
        const folderUid = folder?.uid || null;
        if (!folderUid) {
            this.handleError('Aktuální složka není k dispozici pro nahrání souborů.');
            return;
        }
        if (!this.canUserUploadFiles(folder)) {
            this.handleError('Do kořenové složky nelze nahrávat soubory.');
            return;
        }

        const uploadPanel = this.getApp().getUploadPanel();

        Array.from(arguments[0] || []).forEach((file) => {
            uploadPanel.uploadFile(file, {
                folderUid,
                onSuccess: async () => {
                    await this.reloadCurrentFolder();
                }
            });
        });

    };

    createFolder() {

        const parentFolderUid = this.folderData?.uid || null;
        if (!parentFolderUid) {
            this.handleError('Aktuální složka není k dispozici pro vytvoření podsložky.');
            return;
        }

        const app = this.getApp();
        const browser = this;
        const folderNameInput = new TextBox(null, {
            value: '',
            autofocus: true,
            css: '.responsive'
        });

        const modal = new ModalDialog(null, {
            title: 'Vytvořit složku',
            controls: [
                (
                    new Grid(null, {})
                        .cell({ controls: [new Text(null, { text: 'Zadej název nové složky.' })] })
                        .cell({ controls: [folderNameInput] })
                )
            ],
            buttons: [
                new Button(null, {
                    label: 'Vytvořit',
                    icon: '#confirm',
                    onClick: createConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: '#cancel',
                    css: '.outlined',
                    onClick: createCanceled
                }),
            ]
        });

        modal.show(app);

        function createCanceled() {
            modal.hide();
        };

        async function createConfirmed() {

            const folderName = String(folderNameInput.getValue() || '').trim();
            if (!folderName) {
                browser.handleError('Zadej název nové složky.');
                return;
            }

            modal.hide();
            app.setBusyFlag();

            try {
                await browser.getFileRepositoryManager().createFolder(parentFolderUid, {
                    name: folderName,
                });
                await browser.reloadCurrentFolder();
            } catch (error) {
                browser.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        };

    };

    // --------------------------------------------------------------------------------------------

    async _refresh(options, activeRoute) {

        await super._refresh(options, activeRoute);

        this.isLoading = true;
        this.loadError = null;
        const params = activeRoute?.params || {};

        try {

            let data = [];

            if (params?.uid) {

                data = await Promise.all([
                    this.getFileRepositoryManager().getFolder(params.uid),
                    this.getFileRepositoryManager().getFolderSubfolders(params.uid),
                    this.getFileRepositoryManager().getFolderFiles(params.uid)
                ]);

            } else {

                data = await Promise.all([
                    this.getFileRepositoryManager().getRootFolder(),
                    this.getFileRepositoryManager().getRootFolderSubfolders(),
                    this.getFileRepositoryManager().getRootFolderFiles()
                ]);

            }

            this.folderData = data[0] || null;
            this.folderSubfolders = Array.isArray(data[1]?.items) ? data[1].items : [];
            this.folderFiles = Array.isArray(data[2]?.items) ? data[2].items : [];

            return true;

        } catch (error) {

            this.folderData = null;
            this.folderSubfolders = [];
            this.folderFiles = [];
            this.loadError = true;
            this.handleError(error);
            return false;

        } finally {

            this.isLoading = false;

        }

    };

    // --------------------------------------------------------------------------------------------

    openFolder(folderUid) {

        if (!folderUid) {
            this.getApp().routeTo('file-repository');
            return;
        }

        this.getApp().routeTo('file-repository-folder', { uid: folderUid });

    };

    async openFile(fileUid) {

        const fileContentUrl = this.getFileRepositoryManager().getFileContentUrl(fileUid);
        return openFileContentInNewWindow(fileUid, fileContentUrl, this.getApp(), (error) => this.handleError(error));

    };

    deleteFolder(folderUid) {

        const folder = (this.folderSubfolders || []).find((item) => item?.uid === folderUid);
        if (!folder) {
            this.handleError('Složka nebyla nalezena.');
            return;
        }

        const app = this.getApp();
        const browser = this;

        const modal = new ModalDialog(null, {
            title: i18n.t('module.browser.record_delete_title'),
            controls: [
                new Text(null, { text: `Odstranit složku "${folder.name}"?` }),
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.delete.label'),
                    icon: '#confirm',
                    onClick: deleteConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: '#cancel',
                    css: '.outlined',
                    onClick: deleteCanceled
                }),
            ]
        });

        modal.show(app);

        function deleteCanceled() {
            modal.hide();
        };

        async function deleteConfirmed() {

            modal.hide();
            app.setBusyFlag();

            try {
                await browser.getFileRepositoryManager().deleteFolder(folder.uid);
                await browser.reloadCurrentFolder();
            } catch (error) {
                browser.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        };

    };

    editFolder(folderUid) {

        const folder = (this.folderSubfolders || []).find((item) => item?.uid === folderUid);
        if (!folder) {
            this.handleError('Složka nebyla nalezena.');
            return;
        }

        const app = this.getApp();
        const browser = this;
        const nameInput = new TextBox(null, {
            value: folder?.name || '',
            autofocus: true,
            css: '.responsive'
        });
        const descriptionInput = new TextBox(null, {
            value: folder?.description || '',
            multiline: true,
            css: '.responsive'
        });

        const modal = new ModalDialog(null, {
            title: 'Upravit složku',
            controls: [
                (
                    new Grid(null, {})
                        .cell({ controls: [new Text(null, { text: 'Název složky' })] })
                        .cell({ controls: [nameInput] })
                        .cell({ controls: [new Text(null, { text: 'Popis' })] })
                        .cell({ controls: [descriptionInput] })
                )
            ],
            buttons: [
                new Button(null, {
                    label: 'Uložit',
                    icon: '#confirm',
                    onClick: saveConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: '#cancel',
                    css: '.outlined',
                    onClick: () => modal.hide()
                }),
            ]
        });

        modal.show(app);

        async function saveConfirmed() {

            const name = String(nameInput.getValue() || '').trim();
            if (!name) {
                browser.handleError('Zadej název složky.');
                return;
            }

            modal.hide();
            app.setBusyFlag();

            try {
                await browser.getFileRepositoryManager().updateFolder(folder.uid, {
                    name,
                    description: String(descriptionInput.getValue() || '').trim(),
                });
                await browser.reloadCurrentFolder();
            } catch (error) {
                browser.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        };

    };

    deleteFile(fileUid) {

        const file = (this.folderFiles || []).find((item) => item?.uid === fileUid);
        if (!file) {
            this.handleError('Soubor nebyl nalezen.');
            return;
        }

        const app = this.getApp();
        const browser = this;

        const modal = new ModalDialog(null, {
            title: i18n.t('module.browser.record_delete_title'),
            controls: [
                new Text(null, { text: `Odstranit soubor "${file.name}"?` }),
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.delete.label'),
                    icon: '#confirm',
                    onClick: deleteConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: '#cancel',
                    css: '.outlined',
                    onClick: deleteCanceled
                }),
            ]
        });

        modal.show(app);

        function deleteCanceled() {
            modal.hide();
        };

        async function deleteConfirmed() {

            modal.hide();
            app.setBusyFlag();

            try {
                await browser.getFileRepositoryManager().deleteFile(file.uid);
                await browser.reloadCurrentFolder();
            } catch (error) {
                browser.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        };

    };

    editFile(fileUid) {

        const file = (this.folderFiles || []).find((item) => item?.uid === fileUid);
        if (!file) {
            this.handleError('Soubor nebyl nalezen.');
            return;
        }

        const app = this.getApp();
        const browser = this;
        const nameInput = new TextBox(null, {
            value: file?.name || '',
            autofocus: true,
            css: '.responsive'
        });
        const descriptionInput = new TextBox(null, {
            value: file?.description || '',
            multiline: true,
            css: '.responsive'
        });
        const notesInput = new TextBox(null, {
            value: file?.notes || '',
            multiline: true,
            css: '.responsive'
        });

        const modal = new ModalDialog(null, {
            title: 'Upravit soubor',
            controls: [
                (
                    new Grid(null, {})
                        .cell({ controls: [new Text(null, { text: 'Název souboru' })] })
                        .cell({ controls: [nameInput] })
                        .cell({ controls: [new Text(null, { text: 'Popis' })] })
                        .cell({ controls: [descriptionInput] })
                        .cell({ controls: [new Text(null, { text: 'Poznámky' })] })
                        .cell({ controls: [notesInput] })
                )
            ],
            buttons: [
                new Button(null, {
                    label: 'Uložit',
                    icon: '#confirm',
                    onClick: saveConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: '#cancel',
                    css: '.outlined',
                    onClick: () => modal.hide()
                }),
            ]
        });

        modal.show(app);

        async function saveConfirmed() {

            const name = String(nameInput.getValue() || '').trim();
            if (!name) {
                browser.handleError('Zadej název souboru.');
                return;
            }

            modal.hide();
            app.setBusyFlag();

            try {
                await browser.getFileRepositoryManager().updateFile(file.uid, {
                    name,
                    description: String(descriptionInput.getValue() || '').trim(),
                    notes: String(notesInput.getValue() || '').trim(),
                });
                await browser.reloadCurrentFolder();
            } catch (error) {
                browser.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        };

    };

    // --------------------------------------------------------------------------------------------

    _render(r, vm) {

        const browser = this;

        return r.module(null, [
            r.header(null, renderModuleHeader()),
            r.section(null, renderModuleContent()),
        ]);

        function renderModuleHeader() {

            return [
                renderFolderInfo(),
                renderModuleNav(),
                renderFoldersNav(),
            ];

        };

        function renderFolderInfo() {
            const folderPath = browser.getFolderPath();
            const renderedFolderPath = [];

            folderPath.forEach((folder, index) => {

                if (index > 0)
                    renderedFolderPath.push(' / ');

                const isLast = index === folderPath.length - 1;
                const folderUid = folder?.uid || null;
                const folderName = folder?.name || browser.getCurrentFolderLabel();

                renderedFolderPath.push(
                    isLast
                        ? r.span('bold', [folderName])
                        : r.span({
                            css: 'pointer-cursor',
                            props: { onclick: () => { browser.openFolder(folderUid); } },
                        }, [folderName])
                );

            });

            return r.row({ css: 'border medium-round medium-padding wrap' }, [
                r.span('bold', ['Složka:']),
                ' ',
                ...(
                    renderedFolderPath.length > 0
                        ? renderedFolderPath
                        : [r.span([browser.getCurrentFolderLabel()])]
                ),
            ]);

        };

        function renderModuleNav() {

            return null;

        };

        function renderModuleContent() {

            return [
                browser.panelTabs.render(r, vm),
                renderPanelTab(),
            ];

        };

        function renderPanelTab() {

            if (browser.getActivePanelTab() === FileRepositoryBrowser.TAB_PERMISSIONS)
                return browser.folderPermissionPanel.render(r, vm);

            return r.table({ css: 'lines' }, [
                r.thead([
                    r.trow([
                        r.thcell(['Název']),
                        r.thcell('min', ['Typ']),
                        r.thcell('min right-align', ['Velikost']),
                        r.thcell('min right-align', ['Změněno']),
                        r.thcell('min right-align', ['Akce']),
                    ]),
                ]),
                r.tbody([
                    ...renderFolders(),
                    ...renderFiles(),
                ]),
            ]);

        };

        function renderFoldersNav() {

            const canManageItems = browser.canUserManageItems();
            const canUploadFiles = browser.canUserUploadFiles();
            const uploadTitle = browser.isRootFolder()
                ? 'Do kořenové složky nelze nahrávat soubory.'
                : (canUploadFiles ? null : 'Nemáš oprávnění upravovat obsah této složky.');

            return r.nav(null, [
                r.iconButton('#refresh', {
                    css: 'circle transparent',
                    props: {
                        onclick: () => { browser.reloadCurrentFolder(); }
                    }
                }),
                r.spacer(),
                r.button('Vytvořit složku', '#folder-plus', {
                    props: {
                        disabled: !canManageItems,
                        title: canManageItems ? null : 'Nemáš oprávnění upravovat obsah této složky.',
                        onclick: () => browser.createFolder()
                    }
                }),
                r.uploadButton('Nahrát soubor', '#upload', {
                    multiple: true,
                    disabled: !canUploadFiles,
                    props: {
                        title: uploadTitle,
                    },
                    onValueSet: (files) => browser.uploadFiles(files),
                }),
            ]);

        };

        function renderFolders() {

            let folders = [];

            const parentFolderUid = browser.getParentFolderUid();

            if (parentFolderUid) {
                folders.push(
                    r.trow([
                        r.tcell([
                            r.span({
                                css: 'bold pointer-cursor',
                                props: { onclick: () => { browser.openFolder(parentFolderUid); } },
                            }, [
                                r.icon('arrow-turn-left-up'),
                                ' ',
                                '(nadřazená složka)',
                            ]),
                        ]),
                        r.tcell('min', ['Složka']),
                        r.tcell('min right-align', ['-']),
                        r.tcell('min right-align', ['-']),
                        r.tcell('min right-align', ['-']),
                    ])
                );
            }

            (browser.folderSubfolders || []).forEach((folder) => {

                const userCanOpen = browser.canUserOpenFolder(folder);
                const userCanEdit = browser.canUserEditFolder(folder);
                const userCanDelete = browser.canUserDeleteFolder(folder);
                const folderNameTitle = userCanOpen
                    ? null
                    : 'Nemáš oprávnění otevřít tuto složku.';

                folders.push(
                    r.trow([
                        r.tcell([
                            (
                                userCanOpen
                                    ? r.span({ css: 'bold pointer-cursor', props: { onclick: () => { browser.openFolder(folder.uid); } } }, [
                                        r.icon('folder'),
                                        ' ',
                                        folder.name,
                                    ])
                                    : r.span({ css: 'italic pointer-not-allowed', props: { title: folderNameTitle } }, [
                                        r.icon('folder'),
                                        ' ',
                                        folder.name,
                                    ])
                            ),
                        ]),
                        r.tcell('min', ['Složka']),
                        r.tcell('min right-align', ['-']),
                        r.tcell('min right-align', [
                            dtv.formatDatetime(folder.changedAt) || '-',
                        ]),
                        r.tcell('min right-align', [
                            (
                                userCanEdit
                                    ? r.iconButton('#edit', {
                                        css: 'transparent circle',
                                        props: {
                                            title: 'Upravit složku',
                                            onclick: () => { browser.editFolder(folder.uid); }
                                        }
                                    })
                                    : null
                            ),
                            (
                                userCanDelete
                                    ? r.iconButton('#delete', {
                                        css: 'transparent circle',
                                        props: {
                                            title: 'Odstranit složku',
                                            onclick: () => { browser.deleteFolder(folder.uid); }
                                        }
                                    })
                                    : null
                            ),
                        ]),
                    ])
                );

            });

            return folders;

        };

        function renderFiles() {

            if (browser.isLoading) {
                return [
                    renderStateRow({
                        title: 'Načítám obsah složky',
                        subtitle: 'Počkej chvíli, připravuji aktuální seznam souborů a složek.',
                    }),
                ];
            }

            if (browser.loadError) {
                return [
                    renderStateRow({
                        title: 'Složku se nepodařilo načíst',
                        subtitle: 'Zkus načtení zopakovat nebo zkontroluj oprávnění.',
                    }),
                ];
            }

            if ((browser.folderSubfolders || []).length === 0 && (browser.folderFiles || []).length === 0) {
                return [
                    renderStateRow({
                        title: 'Složka je prázdná',
                        subtitle: 'Ve vybrané složce nejsou žádné podsložky ani soubory.',
                    }),
                ];
            }

            return (browser.folderFiles || []).map((file) => {
                const userCanOpen = browser.canUserOpenFile(file);
                const userCanEdit = browser.canUserEditFile(file);
                const userCanDelete = browser.canUserDeleteFile(file);
                const fileNameTitle = userCanOpen
                    ? null
                    : 'Nemáš oprávnění otevřít tento soubor.';

                return r.trow([
                    r.tcell([
                        (
                            userCanOpen
                                ? r.span({ css: 'pointer-cursor', props: { onclick: () => { browser.openFile(file.uid); } } }, [
                                    r.icon(file.icon || file.fileTypeIcon || 'file'),
                                    ' ',
                                    file.name,
                                ])
                                : r.span({ css: 'italic pointer-not-allowed', props: { title: fileNameTitle } }, [
                                    r.icon(file.icon || file.fileTypeIcon || 'file'),
                                    ' ',
                                    file.name,
                                ])
                        ),
                    ]),
                    r.tcell('min', ['Soubor']),
                    r.tcell('min right-align', [
                        dtv.formatBytes(file.size) || '-',
                    ]),
                    r.tcell('min right-align', [
                        dtv.formatDatetime(file.changedAt) || '-',
                    ]),
                    r.tcell('min right-align', [
                        (
                            userCanEdit
                                ? r.iconButton('#edit', {
                                    css: 'transparent circle',
                                    props: {
                                        title: 'Upravit soubor',
                                        onclick: () => { browser.editFile(file.uid); }
                                    }
                                })
                                : null
                        ),
                        (
                            userCanDelete
                                ? r.iconButton('#delete', {
                                    css: 'transparent circle',
                                    props: {
                                        title: 'Odstranit soubor',
                                        onclick: () => { browser.deleteFile(file.uid); }
                                    }
                                })
                                : null
                        ),
                    ]),
                ]);
            });

        };

        function renderStateRow({ title, subtitle }) {

            return r.trow([
                r.tcell({ props: { colspan: 5 } }, [
                    r.empty({
                        title,
                        subtitle,
                        icon: 'folder-open',
                    }),
                ]),
            ]);

        }

    }

}
